import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import useScheme from "../../providers/scheme_provider";
import ChipsWithAggregation from "../../../../components/chips_with_aggregation/chips_with_aggregation";
import { SchemeOtherDetailsInputs } from "../../models/scheme_form";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import HTMLEditor from "../../../../components/html_editor/html_editor";
import { black, purple, purpleLight } from "../../../../theme/colors";
import RequiredMark from "../../../../components/required_mark/required_mark";

export default function OtherSchemeInformation({
  otherDetails,
  goBack,
  onSubmit: _onSubmit,
}: {
  otherDetails: SchemeOtherDetailsInputs;
  goBack: () => void;
  onSubmit: (data: SchemeOtherDetailsInputs) => void;
}) {
  const { schemeMaster } = useScheme();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SchemeOtherDetailsInputs>({
    defaultValues: otherDetails,
  });

  const onSubmit: SubmitHandler<SchemeOtherDetailsInputs> = (data) =>
    _onSubmit(data);

  return (
    <Box p={2}>
      <Box mt={2} mb={2}>
        <Typography variant="subtitle1" fontWeight={700} color={black}>
          Other Scheme Information
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item md={6}>
            <Box>
              <Typography variant="body2" color="primary.main" mb={0.5}>
                Scheme Application Process<RequiredMark />
              </Typography>
              <Controller
                name="applicationProcess"
                control={control}
                rules={{ required: "Select the process mode*" }}
                render={({ field: { onChange, value } }) => (
                  <Box>
                    {schemeMaster?.schemeProcessMode?.map((process) => (
                      <Chip
                        key={process.id}
                        onClick={() => onChange(process.id)}
                        label={process.value}
                        variant={value === process.id ? "filled" : "outlined"}
                        color="primary"
                        sx={{ mr: 1 }}
                      />
                    ))}
                    {!schemeMaster?.schemeProcessMode?.length && (
                      <Typography variant="body2" color="text.secondary">
                        No application process options are configured.
                      </Typography>
                    )}
                  </Box>
                )}
              />
              {!!errors.applicationProcess && (
                <Typography variant="caption" color="error">
                  {errors.applicationProcess?.message}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Application Process Details
            </Typography>
            <Controller
              name="applicationProcessDetails"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter Scheme Application Process Details (Optional)"
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Summary
            </Typography>
            <Controller
              name="summary"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter summary details (Optional)"
                />
              )}
            />
            {!!errors.summary && (
              <Typography variant="caption" color="error">
                {errors.summary?.message}
              </Typography>
            )}
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Details<RequiredMark />
            </Typography>
            <Controller
              name="details"
              control={control}
              rules={{ required: "Enter scheme details" }}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter scheme details"
                />
              )}
            />
            {!!errors.details && (
              <Typography variant="caption" color="error">
                {errors.details?.message}
              </Typography>
            )}
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Benefits
            </Typography>
            <Controller
              name="benefits"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter Scheme Benefits (Optional)"
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Scheme Exclusions
            </Typography>
            <Controller
              name="exclusions"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter Scheme Exclusions (Optional)"
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Eligibility Description
            </Typography>
            <Controller
              name="eligibility"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter eligibility description (Optional)"
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Typography variant="body2" color="primary.main" mb={0.5}>
              Documents Required
            </Typography>
            <Controller
              name="documentRequired"
              control={control}
              render={({ field: { onChange, value } }) => (
                <HTMLEditor
                  onChange={onChange}
                  value={value}
                  placeholder="Enter documents required (Optional)"
                />
              )}
            />
          </Grid>
        </Grid>
        <Box mt={2} mb={2}>
          <Divider />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            sx={{
              pl: 6,
              pr: 6,
              borderColor: purple,
              color: purple,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: purple, bgcolor: purpleLight },
            }}
            onClick={goBack}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            disableElevation
            sx={{
              pl: 6,
              pr: 6,
              bgcolor: purple,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
          >
            Next
          </Button>
        </Box>
      </form>
    </Box>
  );
}
